import type { StrictPaddingStyle } from '../util/types';

import { SelectionIcon } from './SelectionIcon';
import { Text } from '../Text';
import { useTheme } from '../ThemeProvider';
import { useAnimatedNative } from '../util/useAnimatedNative';
import { useFocusRing } from '../util/useFocusRing';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type ViewStyle , Animated, Platform, Pressable, StyleSheet, type TextStyle, View } from 'react-native';


export type CheckChipSize = 'sm' | 'lg';

export type CheckChipStyle = StrictPaddingStyle &
    Pick<ViewStyle, 'alignSelf'>;

export interface CheckChipProps {
    /**
     * The text to display in the chip
     */
    label: string;

    /**
     * Whether the chip is selected/checked
     */
    checked?: boolean;

    /**
     * Size of the chip
     */
    size?: CheckChipSize;

    /**
     * Callback when the chip is pressed
     */
    onPress?: () => void;

    /**
     * Disables the chip interaction
     */
    disabled?: boolean;

    /**
     * Whether the chip is invalid
     */
    invalid?: boolean;

    /**
     * Whether the chip is part of a multi-select group
     */
    multiSelect?: boolean;

    /**
     * Test ID for testing purposes
     */
    testID?: string;

    /**
     * Defines the padding style overrides.
     */
    sx?: CheckChipStyle;
}

export function CheckChip({
    label,
    checked = false,
    size = 'lg',
    onPress,
    disabled = false,
    invalid = false,
    multiSelect = true,
    testID,
    sx,
}: CheckChipProps): React.JSX.Element {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const { isFocusVisible, focusProps } = useFocusRing();
    const styles = useMemo(
        () =>
            StyleSheet.create({
                container: {
                    borderRadius: theme.borderRadius.lg,
                    alignSelf: 'flex-start',
                },
                containerUnchecked: {
                    backgroundColor: theme.colors.backgroundWeak,
                },
                containerChecked: {
                    backgroundColor: theme.colors.background,
                    borderColor: theme.colors.foregroundStrong,
                },
                invalid: {
                    borderColor: theme.colors.backgroundStatusCritical,
                },
                disabled: {
                    opacity: 0.4,
                },
                containerUncheckedHovered: {
                    backgroundColor: theme.colors.backgroundActionWeakHover,
                },
                containerCheckedHovered: {
                    backgroundColor: theme.colors.backgroundActionHover,
                },
                content: {
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing.scb1,
                },
            }),
        [theme],
    );

    const unselectedBorderWidth = invalid ? 1 : 0;
    const selectedBorderWidth = 2;
    const iconShown = multiSelect || checked;

    const backgroundColorAnim = useRef(new Animated.Value(checked ? 1 : 0)).current;
    const iconScaleAnim = useRef(new Animated.Value(iconShown ? 1 : 0)).current;

    useEffect(() => {
        const duration = 200;
        const animation = Animated.parallel([
            Animated.timing(backgroundColorAnim, {
                toValue: checked ? 1 : 0,
                duration,
                useNativeDriver: false,
            }),
        ]);

        animation.start();

        return () => {
            animation.stop();
        };
    }, [checked, backgroundColorAnim]);

    const useNativeDriver = useAnimatedNative();

    useEffect(() => {
        const animation = Animated.spring(iconScaleAnim, {
            toValue: iconShown ? 1 : 0,
            useNativeDriver: useNativeDriver,
            tension: 200,
            friction: 10,
        });

        animation.start();

        return () => {
            animation.stop();
        };
    }, [iconShown, iconScaleAnim, useNativeDriver]);

    const animatedBackgroundColor = backgroundColorAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [theme.colors.backgroundWeak, theme.colors.background],
    });

    const borderWidth = checked ? selectedBorderWidth : unselectedBorderWidth;
    const paddingTop = size === 'sm' ? 6 : 9;
    const paddingBottom = size === 'sm' ? 7 : 11;
    const paddingEnd = size === 'sm' ? theme.spacing.s4 : theme.spacing.s5;
    const paddingStart = size === 'sm' ? theme.spacing.s4 : theme.spacing.s5;
    const paddingStartIcon = size === 'sm' ? theme.spacing.s2 : theme.spacing.s4;

    const getContainerStyle = (hovered: boolean) => [
        styles.container,
        {
            backgroundColor: animatedBackgroundColor,
            paddingStart: iconShown ? paddingStartIcon : paddingStart,
            paddingEnd: paddingEnd,
            paddingTop: paddingTop - borderWidth,
            paddingBottom: paddingBottom - borderWidth,
            borderWidth: borderWidth,
            borderColor: theme.colors.foregroundStrong,
        },
        invalid && styles.invalid,
        disabled && styles.disabled,
        Platform.OS === 'web' &&
            hovered &&
            (checked ? styles.containerCheckedHovered : styles.containerUncheckedHovered),
        sx,
    ];

    const fontWeight = checked ? theme.fontWeights.bold : theme.fontWeights.normal;
    
    return (
        <Pressable
            onPress={onPress}
            disabled={disabled}
            testID={testID}
            accessibilityRole="checkbox"
            accessibilityState={{ checked }}
            aria-checked={checked}
            aria-invalid={invalid}
            role="checkbox"
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            onFocus={focusProps.onFocus}
            onBlur={focusProps.onBlur}
            style={isFocusVisible && Platform.OS === 'web' ? {
                    outlineStyle: 'solid',
                    outlineWidth: 2,
                    outlineColor: theme.colors.utilityFocus,
                    outlineOffset: 2,
                } : undefined}
        >
            <Animated.View style={getContainerStyle(isHovered)}>
                <View style={styles.content}>
                    {iconShown && (
                        <Animated.View style={{ transform: [{ scale: iconScaleAnim }] }}>
                            <SelectionIcon checked={checked} testID="selection-icon" />
                        </Animated.View>
                    )}
                    <Text
                        level={3}
                        sx={{
                            color: theme.colors.foregroundStrong,
                            fontWeight: String(fontWeight) as TextStyle['fontWeight'],
                            flexShrink: 1,
                        }}
                    >
                        {label}
                    </Text>
                </View>
            </Animated.View>
        </Pressable>
    );
}
